import {
  toWSDateString,
  WSAlert,
  WSButton,
  WSDivider,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSGrid,
  WSPill
} from "@wingspanhq/fe-component-library";
import { ICustomField } from "@wingspanhq/payments/dist/interfaces";
import { IContractorPayoutsSummaryReportResponse } from "@wingspanhq/payments/dist/interfaces/api/reports";
import React, { useCallback, useEffect, useState } from "react";
import { useUserId } from "../../../../query/hooks/helpers";
import { IContractorPayoutsSummaryReportRequest } from "../../../../query/payments/queries";
import { paymentsService } from "../../../../services/payments";
import { HeroRangeSelect } from "../../../../shared/components/HeroRangeSelect/HeroRangeSelect";
import { VerticalDivider } from "../../../../shared/components/VerticalDivider";
import { useAuthorizedScopeGroups } from "../../../../shared/utils/teamUtils";
import { track } from "../../../../utils/analytics";
import { DownloadReportButton } from "../../components/DownloadReportButton/DownloadReportButton";
import { useSelectColumnsModal } from "../../components/ModalSelectColumns";
import { ReportsTable } from "../../components/ReportsTable/ReportsTable";
import { useFetchReportData } from "../../hooks/useFetchReportData";
import { useReportColumns } from "../../hooks/useReportColumns";
import { ReportFormat, ReportType } from "../../types";
import { formatReportsValue } from "../../utils/formatReportsValue";
import { selectorColumnsWithAdditionalData } from "../../utils/selectorColumnsWithAdditionalData";
import { selectorTableColumns } from "../../utils/selectorTableColumns";
import { CardReportLoading } from "../../components/CardReportLoading/CardReportLoading";
import { getColumns } from "./columns";

const COLUMNS_STORAGE_KEY =
  "REPORTS_COLLABORATOR_PAYABLES_SUMMARY_COLUMNS_STORAGE_KEY";

export const CollaboratorPayablesSummaryDashboard: React.FC<{
  collaboratorsAdditionalData: ICustomField[];
}> = ({ collaboratorsAdditionalData }) => {
  const { hasAdminScope } = useAuthorizedScopeGroups();
  const userId = useUserId();
  const [selectedDateRange, setSelectedDateRange] = useState<[Date, Date]>([
    new Date(new Date().getFullYear(), 0, 1, 0, 0, 0, 0),
    new Date(new Date().getFullYear(), 11, 31, 23, 59, 59, 999)
  ]);
  const [showCompletionAlert, setShowCompletionAlert] = useState(false);

  const columns =
    selectorColumnsWithAdditionalData<IContractorPayoutsSummaryReportResponse>(
      getColumns(hasAdminScope),
      collaboratorsAdditionalData,
      { prefixGroupName: false }
    );

  const getColumnByKey = (key: string) =>
    columns.find(_column => key === _column.value)!;

  const [selectedColumns, setSelectedColumns] = useReportColumns(
    COLUMNS_STORAGE_KEY,
    userId,
    columns
  );

  const modalSelectColumns = useSelectColumnsModal();

  const selectedRangeKey = selectedDateRange
    .map(d => toWSDateString(d, "monthDayYear"))
    .join(" - ");

  const isRunButtonDisabled = !selectedDateRange || !selectedColumns.length;

  const filters: IContractorPayoutsSummaryReportRequest = {
    paidFromDate: selectedDateRange[0],
    paidToDate: selectedDateRange[1]
  };

  const request = useCallback(
    async (pageSize, pageNumber) => {
      return await paymentsService.reports.collaboratorPayable(filters, {
        page: {
          size: pageSize,
          number: pageNumber
        }
      });
    },
    [filters.paidFromDate, filters.paidToDate]
  );

  const { query, totalRowsCount, processedRowsCount } = useFetchReportData<
    IContractorPayoutsSummaryReportResponse,
    {
      summary: { listSize: number };
      data: IContractorPayoutsSummaryReportResponse[];
    }
  >(["QUERY_COLLABORATOR_PAYABLES_SUMMARY", filters], request);

  // Check if report has just completed
  React.useEffect(() => {
    if (
      !query.isFetching &&
      totalRowsCount > 0 &&
      processedRowsCount === totalRowsCount
    ) {
      setShowCompletionAlert(true);
    }
  }, [processedRowsCount, query.isFetching, totalRowsCount]);

  useEffect(() => {
    if (query.isFetching) {
      setShowCompletionAlert(false);
    }
  }, [query.isFetching]);

  useEffect(() => {
    setShowCompletionAlert(false);
  }, [selectedDateRange]);

  const tableData = query.data || [];
  const isDownloadButtonDisabled = tableData.length === 0 || query.isFetching;

  const getReportJSON = () => {
    if (!tableData.length) {
      return [];
    }
    return [
      selectedColumns.map(getColumnByKey).map(c => c?.label),
      ...tableData
        .map(data => {
          return selectedColumns.map(column => {
            let result;
            const currentColumn = getColumnByKey(column);

            try {
              result = formatReportsValue(currentColumn?.getTableCell?.(data));
            } catch (e) {
              console.error(`REPORT ERROR! "${currentColumn?.label}": `, e);
              result = undefined;
            }

            return result;
          });
        })
        .filter(arr => arr.some(Boolean))
    ];
  };

  return (
    <>
      <WSDivider mb="M" />
      <WSGrid>
        <WSGrid.Item span={{ s: "12" }}>
          <WSFlexBox.CenterY>
            <WSElement>
              <HeroRangeSelect
                color="blue500"
                position={"right"}
                customLabel={selectedRangeKey}
                onChange={data => {
                  setSelectedDateRange(data.value);
                }}
                value={selectedDateRange}
              />
            </WSElement>
            <VerticalDivider mr="XL" />
            <WSFlexBox.CenterY
              onClick={() =>
                modalSelectColumns.open({
                  selectedColumns,
                  columns,
                  onSubmit: ({ columns }) => {
                    setSelectedColumns(columns);
                  }
                })
              }
              data-testid="selectColumns"
            >
              <WSButton.Link icon="menu">Selected columns</WSButton.Link>
              <WSPill ml="M" theme="blue" text={`${selectedColumns.length}`} />
            </WSFlexBox.CenterY>

            <VerticalDivider mx="XL" />
            <WSButton.Primary
              name="runReport"
              disabled={isRunButtonDisabled}
              onAsyncClick={async () => {
                track("Report Ran", {
                  reportName: ReportType.CollaboratorPayablesSummary,
                  selectedColumnsCount: selectedColumns.length,
                  selectedColumns,
                  filters
                });
                await query.refetchData();
              }}
            >
              Run report
            </WSButton.Primary>
          </WSFlexBox.CenterY>
        </WSGrid.Item>
      </WSGrid>
      <WSDivider mt="M" />
      <WSFlexBox.CenterY mt="2XL" mb="XL" justify="flex-end">
        <DownloadReportButton
          disabled={isDownloadButtonDisabled}
          getData={getReportJSON}
          onClick={(format: ReportFormat) => {
            track("Report Downloaded", {
              reportName: ReportType.CollaboratorPayablesSummary,
              selectedColumnsCount: selectedColumns.length,
              selectedColumns,
              filters,
              format
            });
          }}
          fileName={`Wingspan Contractor Report`}
          sheetName={selectedRangeKey}
        />
      </WSFlexBox.CenterY>

      {showCompletionAlert && (
        <WSAlert
          mb="2XL"
          theme="success"
          icon="check-circle"
          onDismiss={() => setShowCompletionAlert(false)}
        >
          Report complete
        </WSAlert>
      )}

      {query.isFetching && (
        <CardReportLoading
          processedRowsCount={processedRowsCount}
          totalRowsCount={totalRowsCount}
        />
      )}

      {tableData.length ? (
        <ReportsTable<IContractorPayoutsSummaryReportResponse>
          mb="M"
          mt="XL"
          columns={selectorTableColumns(
            selectedColumns.map(getColumnByKey).filter(Boolean)
          )}
          tableData={tableData.map((data, i) => ({
            data,
            id: `id_${i}_${data.payeeId}`
          }))}
        />
      ) : (
        <WSEmptyState
          type="documents"
          title="No report to show yet"
          description={
            query.data?.length === 0
              ? "No contractors with payments found in this date range"
              : "Adjust period range then click 'Run report'"
          }
          orientation="horizontal"
          hasBorder
        />
      )}
    </>
  );
};
